import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Eye, EyeOff, AlertCircle, ChevronRight, Shield, Loader, Copy, RefreshCw, Clock, Clipboard } from 'lucide-react';
import {
  Card,
  Button,
  Input,
  Textarea,
  TaskModeSwitcher,
  BatchTaskForm,
  ProgressSteps,
  BatchTaskProgress,
  ConfirmationModal,
  RetryModal,
  SuccessRecordsModal,
  LanguageSwitcher
} from './components';
import {
  useLogs,
  useTaskProcessor,
  useBatchTaskProcessor,
  useBranding
} from './hooks';
import { isValidRedeemCode, extractTokenFromInput } from './utils';
import { EXTERNAL_LINKS } from './constants';
import './App.css';

const TaskProcessor = () => {
  const { t } = useTranslation();
  const [taskMode, setTaskMode] = useState('single');
  const [showSuccessRecords, setShowSuccessRecords] = useState(false);
  const [showSessionHelp, setShowSessionHelp] = useState(false);

  const [copyPrefixSuccess, setCopyPrefixSuccess] = useState(false);

  const showBranding = useBranding();
  
  const {
    logs,
    showLogs,
    copySuccess,
    logContainerRef,
    addLog,
    clearLogs,
    copyLogs,
    setShowLogs
  } = useLogs();

  const {
    isProcessing: isSingleProcessing,
    error: singleError,
    accountInput,
    redeemCode,
    extractedToken,
    taskStatus,
    taskId,
    taskError,
    taskResult,
    taskRetryCount,
    isValidatingToken,
    isValidatingCode,
    tokenValid,
    codeValid,
    userInfo,
    accountInfo,
    showConfirmation,
    showRetryModal,
    isOverwrite,
    setAccountInput,
    setRedeemCode,
    setError: setSingleError,
    setShowConfirmation,
    setShowRetryModal,
    validateToken,
    validateRedeemCode,
    runPrecheck,
    handleSubmit,
    handleRetryConfirm,
    resetForm
  } = useTaskProcessor(addLog);

  const {
    batchTasks,
    isAllTasksCompleted,
    isValidatingBatch,
    handleBatchSubmit,
    resetBatchTasks,
    setIsValidatingBatch
  } = useBatchTaskProcessor(addLog);

  const isProcessing = isSingleProcessing || (taskMode === 'batch' && batchTasks.some(t => t.status === 'processing'));

  // 任务模式切换时重置状态
  const handleTaskModeChange = (newMode) => {
    setTaskMode(newMode);
    setSingleError(null);
    addLog(`${t('common.info')}: ${t('taskMode.' + newMode)}`, 'info');
  };

  const accountPrefix = useMemo(() => {
    const input = accountInput?.trim();
    if (!input || !input.includes('----')) return null;
    const parts = input.split('----');
    if (parts.length < 2) return null;
    return parts.slice(0, -1).join('----');
  }, [accountInput]);

  const copyPrefix = async () => {
    if (!accountPrefix) return;
    try {
      await navigator.clipboard.writeText(accountPrefix);
      setCopyPrefixSuccess(true);
      setTimeout(() => setCopyPrefixSuccess(false), 1500);
    } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* 标题栏 */}
      <header className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl font-bold tracking-tight">{t('app.title')}</h1>
              <p className="text-indigo-100 text-xs mt-0.5">{t('app.subtitle')}</p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                size="sm"
                variant="header-outline"
                onClick={() => setShowSuccessRecords(true)}
              >
                <CheckCircle className="mr-1 h-3 w-3" />
                {t('header.rechargeRecords')}
              </Button>
              <Button
                size="sm"
                variant="header-outline"
                onClick={() => setShowLogs(!showLogs)}
              >
                {showLogs ? <EyeOff className="mr-1 h-3 w-3" /> : <Eye className="mr-1 h-3 w-3" />}
                {showLogs ? t('header.hideLogs') : t('header.showLogs')}
              </Button>
              <LanguageSwitcher />
              {showBranding && (
                <div className="opacity-30 text-white font-light italic text-sm">
                  Lianduang
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-4 flex-grow">
        {/* 任务模式切换器 */}
        <TaskModeSwitcher mode={taskMode} onChange={handleTaskModeChange} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* 表单区域 */}
          <div className={showLogs ? "lg:col-span-2" : "lg:col-span-3"}>
            {taskMode === 'single' ? (
              <Card title={t('form.accountInfo')} subtitle={t('form.redeemCodePlaceholder')}>
                {/* 账号输入框 */}
                <div className="mb-3">
                  <label htmlFor="account" className="block text-sm font-medium text-slate-700 mb-1.5 flex justify-between">
                    <span>{t('form.accountInfo')}</span>
                    <button
                      onClick={() => setShowSessionHelp(!showSessionHelp)}
                      className="text-indigo-600 hover:text-indigo-700 text-xs flex items-center"
                    >
                      <Shield className="h-3 w-3 mr-1" />
                      {t('form.getAccountInfo')}
                    </button>
                  </label>

                  {/* 显示获取Session的帮助信息 */}
                  {showSessionHelp && (
                    <div className="mb-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-800">
                      <h3 className="font-medium mb-1.5">{t('help.title')}</h3>
                      <p className="text-xs text-indigo-700 mb-3"><strong>{t('common.warning')}:</strong> {t('help.warning')}</p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
                        <div>
                          <h4 className="text-xs font-medium mb-2 text-indigo-800">📋 {t('help.steps')}</h4>
                          <ol className="space-y-2 text-xs text-indigo-700 pl-4 list-decimal">
                            <li className="leading-relaxed">
                              {t('help.step1')}
                              <a
                                href={EXTERNAL_LINKS.SESSION_API}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-indigo-600 mx-1 inline-flex items-center underline"
                              >
                                {t('help.step1Link')}
                                <Shield className="ml-1 h-3 w-3" />
                              </a>
                            </li>
                            <li className="leading-relaxed">
                              <strong>{t('help.step2')}</strong>
                            </li>
                            <li className="text-green-700 leading-relaxed">
                              💡 <strong>{t('common.info')}:</strong> {t('help.step3')}
                            </li>
                          </ol>
                        </div>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <h4 className="text-xs font-medium mb-2 text-yellow-800">⚠️ {t('help.troubleshoot')}</h4>
                          <div className="space-y-2 text-xs text-yellow-700">
                            <p><strong>{t('help.emptyPage')}</strong></p>
                            <p>{t('help.relogin')} <a href={EXTERNAL_LINKS.CHATGPT_URL} target="_blank" rel="noopener noreferrer" className="underline text-yellow-600">{t('help.reloginLink')}</a> {t('help.reloginSuffix')}</p>
                            <p className="text-xs text-gray-600">💡 {t('help.appUsers')}</p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 pt-3 border-t border-indigo-200">
                        <h4 className="text-xs font-medium mb-2 text-indigo-800">📝 {t('help.supportedFormats')}</h4>
                        <div className="text-xs text-indigo-700 bg-indigo-100 p-3 rounded-lg font-mono space-y-1">
                          <div>✅ <strong>{t('help.format1').split(':')[0]}</strong>：{`{"accessToken":"eyJhbGc...","user":{"email":"xxx"}}`}</div>
                          <div>✅ <strong>{t('help.format2').split(':')[0]}</strong>：eyJhbGc...</div>
                          <div>✅ <strong>{t('help.format3').split(':')[0]}</strong>：邮箱----accessToken</div>
                        </div>
                      </div>
                    </div>
                  )}

                  <Textarea
                    value={accountInput}
                    onChange={(e) => setAccountInput(e.target.value)}
                    placeholder={t('form.accountPlaceholder')}
                    className="font-mono overflow-x-auto whitespace-nowrap"
                    disabled={isProcessing}
                  />
                  
                  <div className="mt-1.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex-shrink-0 flex items-center gap-2">
                      {extractedToken.value && (
                        <div className="p-1.5 rounded-md bg-slate-50 border border-slate-200 flex items-center">
                          <span className="text-xs text-slate-600">已识别：</span>
                          <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                            extractedToken.type.includes('未知') 
                              ? 'bg-red-100 text-red-700' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {extractedToken.type}
                          </span>
                        </div>
                      )}
                      {accountPrefix && (
                        <button
                          onClick={copyPrefix}
                          className="p-1.5 rounded-md bg-slate-50 border border-slate-200 hover:bg-slate-100 flex items-center text-xs text-slate-600 transition-colors"
                          title={`复制: ${accountPrefix}`}
                        >
                          {copyPrefixSuccess ? (
                            <><CheckCircle className="h-3 w-3 mr-1 text-green-500" />已复制</>
                          ) : (
                            <><Clipboard className="h-3 w-3 mr-1" />复制账号前缀</>
                          )}
                        </button>
                      )}
                    </div>

                    <div className="flex-shrink-0">
                      {extractedToken.value && !tokenValid && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={validateToken}
                          disabled={isValidatingToken || isProcessing}
                          className="bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
                        >
                          {isValidatingToken ? (
                            <>
                              <Loader className="animate-spin h-3 w-3 mr-1" />
                              {t('form.verifying')}
                            </>
                          ) : (
                            <>
                              <Shield className="h-3 w-3 mr-1" />
                              {t('form.verifyToken')}
                            </>
                          )}
                        </Button>
                      )}

                      {tokenValid && userInfo && (
                        <div className="p-1.5 rounded-md bg-green-50 border border-green-200 flex items-center">
                          <CheckCircle className="text-green-500 h-3.5 w-3.5 mr-1" />
                          <span className="text-xs text-green-700">已验证: {userInfo.email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* 兑换码输入框 */}
                <div className="relative">
                  <Input
                    label={t('form.redeemCode')}
                    value={redeemCode}
                    onChange={(e) => {
                      let newValue = e.target.value;
                      if (newValue.includes('?card=')) {
                        const match = newValue.match(/[?&]card=([A-Za-z0-9]+)/);
                        if (match) {
                          newValue = match[1];
                        }
                      }
                      setRedeemCode(newValue.toUpperCase().trim());
                    }}
                    placeholder={t('form.redeemCodePlaceholder')}
                    maxLength={100}
                    disabled={isProcessing}
                    className={`${redeemCode && !isValidRedeemCode(redeemCode) ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : ''}`}
                  />
                  
                  {/* 输入框状态指示器 */}
                  {redeemCode && (
                    <div className="absolute right-3 top-8 flex items-center">
                      {isValidRedeemCode(redeemCode) ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                  )}
                </div>
                
                {/* 验证状态和按钮区域 */}
                <div className="mt-2 space-y-2">
                  {redeemCode && (
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center text-xs">
                        {isValidRedeemCode(redeemCode) ? (
                          <span className="text-green-600 flex items-center">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            格式正确
                          </span>
                        ) : (
                          <span className="text-red-600 flex items-center">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            格式无效，请检查兑换码
                          </span>
                        )}
                      </div>

                      {isValidRedeemCode(redeemCode) && !codeValid && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={validateRedeemCode}
                          disabled={isValidatingCode || isProcessing}
                          className="bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
                        >
                          {isValidatingCode ? (
                            <>
                              <Loader className="animate-spin h-3 w-3 mr-1" />
                              验证中...
                            </>
                          ) : (
                            <>
                              <Shield className="h-3 w-3 mr-1" />
                              验证兑换码
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  )}

                  {codeValid && (
                    <div className="p-3 rounded-lg bg-green-50 border border-green-200 flex items-center">
                      <CheckCircle className="text-green-500 h-4 w-4 mr-2 flex-shrink-0" />
                      <span className="text-sm text-green-700 font-medium">兑换码验证成功</span>
                    </div>
                  )}
                </div>

                {/* 错误提示 */}
                {singleError && (
                  <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-start">
                    <AlertCircle className="text-red-500 mr-2 h-4 w-4 mt-0.5 flex-shrink-0" />
                    <p className="text-red-700 text-sm">{singleError}</p>
                  </div>
                )}

                {/* 操作按钮 */}
                <div className="flex gap-3">
                  <div className="button-container" title={(!accountInput || !redeemCode) ? '请输入账号信息和兑换码' : ''}>
                    <button 
                      className={`brutalist-button openai ${isProcessing || !accountInput || !redeemCode || isValidatingToken || isValidatingCode ? 'disabled' : ''} ${isProcessing ? 'processing' : ''}`}
                      onClick={runPrecheck}
                      disabled={isProcessing || !accountInput || !redeemCode || isValidatingToken || isValidatingCode}
                    >
                      <div className="openai-logo">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          className="openai-icon"
                        >
                          <path
                            fill="#10A37F"
                            d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.8956zm16.0993 3.8558L12.5907 8.3829 14.6108 7.2144a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.3927-.6813zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"
                          ></path>
                        </svg>
                      </div>
                      <div className="button-text">
                        {isProcessing ? (
                          <>
                            <span>充值中...</span>
                            <span>请稍候</span>
                          </>
                        ) : (
                          <>
                            <span>即刻充值您的</span>
                            <span>GPT-Plus!</span>
                          </>
                        )}
                      </div>
                    </button>
                  </div>

                  <Button
                    variant="secondary"
                    onClick={resetForm}
                    disabled={isProcessing}
                  >
                    {t('form.reset')}
                  </Button>
                </div>
              </Card>
            ) : (
              // 批量任务界面
              <Card title="批量充值任务" subtitle="批量提交多个账号充值任务">
                <BatchTaskForm
                  isProcessing={isProcessing}
                  isValidating={isValidatingBatch}
                  onSubmit={handleBatchSubmit}
                  addLog={addLog}
                  setError={setSingleError}
                  isAllTasksCompleted={isAllTasksCompleted}
                />
              </Card>
            )}

            {/* 单个任务状态卡片 */}
            {taskMode === 'single' && (taskId || taskStatus) && (
              <Card title="任务状态" className="mt-3">
                <div className="flex items-center gap-2 mb-4">
                  {taskRetryCount > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                      重试: {taskRetryCount}/3
                    </span>
                  )}
                </div>

                {/* 进度条 */}
                <ProgressSteps 
                  currentStep={taskStatus || 'pending'} 
                  isProcessing={isProcessing || taskStatus === 'retrying'} 
                  hasError={taskStatus === 'failed' || taskStatus === 'timeout'}
                />

                <div className="flex flex-col gap-2 text-sm">
                  {taskId && (
                    <div className="grid grid-cols-4 gap-2">
                      <div className="col-span-1 font-medium text-slate-500">任务ID:</div>
                      <div className="col-span-3 font-mono text-slate-800 bg-slate-50 p-1.5 rounded border border-slate-200 text-xs overflow-x-auto">
                        {taskId}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-4 gap-2">
                    <div className="col-span-1 font-medium text-slate-500">兑换码:</div>
                    <div className="col-span-3 font-mono text-slate-800">{redeemCode}</div>
                  </div>

                  <div className="grid grid-cols-4 gap-2">
                    <div className="col-span-1 font-medium text-slate-500">邮箱:</div>
                    <div className="col-span-3 text-slate-800">{userInfo?.email || '未知'}</div>
                  </div>

                  {taskStatus === 'completed' && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg flex items-start">
                      <CheckCircle className="text-green-500 mr-2 h-4 w-4 mt-0.5 flex-shrink-0" />
                      <p className="text-green-700 text-sm">{taskResult || '充值已成功完成'}</p>
                    </div>
                  )}

                  {taskStatus === 'retrying' && (
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-start">
                      <RefreshCw className="text-amber-500 mr-2 h-4 w-4 mt-0.5 flex-shrink-0 animate-spin" />
                      <p className="text-amber-700 text-sm">渠道处理失败，正在自动切换其他渠道重试...</p>
                    </div>
                  )}

                  {(taskStatus === 'failed' || taskStatus === 'timeout') && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg flex items-start">
                      <AlertCircle className="text-red-500 mr-2 h-4 w-4 mt-0.5 flex-shrink-0" />
                      <p className="text-red-700 text-sm">{taskError || '充值失败，请重试'}</p>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* 批量任务进度 */}
            {taskMode === 'batch' && batchTasks.length > 0 && (
              <BatchTaskProgress tasks={batchTasks} onReset={resetBatchTasks} />
            )}
          </div>

          {/* 右侧：日志区域 */}
          {showLogs && (
            <div className="lg:col-span-1">
              <Card title={t('logs.title')}>
                <div className="flex gap-1.5 mb-3">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={copyLogs}
                  >
                    <Copy className="mr-1 h-3 w-3" />
                    {copySuccess ? t('logs.copied') : t('logs.copy')}
                  </Button>

                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={clearLogs}
                  >
                    <RefreshCw className="mr-1 h-3 w-3" />
                    {t('logs.clear')}
                  </Button>
                </div>

                <div
                  ref={logContainerRef}
                  className="bg-slate-50 p-2 h-[calc(100vh-16rem)] overflow-y-auto font-mono text-xs"
                >
                  {logs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-slate-400">
                      <Clock className="h-8 w-8 mb-2 opacity-30" />
                      <p className="text-center text-sm">{t('logs.empty')}</p>
                    </div>
                  ) : (
                    logs.map((log, index) => (
                      <div key={index} className={`mb-1.5 flex items-start ${
                        log.type === 'error' ? 'text-red-700' :
                        log.type === 'success' ? 'text-green-700' :
                        'text-slate-700'
                      }`}>
                        <span className="text-slate-500 mr-2 flex-shrink-0">{log.timestamp}</span>
                        <span className="flex-grow break-words">
                          {log.type === 'error' && <span className="text-red-500 mr-1">✕</span>}
                          {log.type === 'success' && <span className="text-green-500 mr-1">✓</span>}
                          {log.type === 'info' && <span className="text-blue-500 mr-1">ⓘ</span>}
                          {log.message}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </div>
          )}
        </div>
      </main>

      {/* 底部 */}
      <footer className="bg-slate-800 text-white py-3">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            <div className="text-sm">
              <span className="text-slate-300">{t('app.footer')}</span>
            </div>
            {showBranding && (
              <div className="text-slate-400 text-xs">
                © 2025 Lian Duang
              </div>
            )}
          </div>
        </div>
      </footer>

      {/* 模态框 */}
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleSubmit}
        userInfo={userInfo}
        accountInfo={accountInfo}
        redeemCode={redeemCode}
        isOverwrite={isOverwrite}
      />

      <RetryModal
        isOpen={showRetryModal}
        onClose={() => setShowRetryModal(false)}
        onRetry={handleRetryConfirm}
        errorMessage={taskError}
      />

      <SuccessRecordsModal
        isOpen={showSuccessRecords}
        onClose={() => setShowSuccessRecords(false)}
      />
    </div>
  );
};

export default TaskProcessor;